import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from "react";
import { Input, Modal } from "semantic-ui-react";
import CardPatientSearchCU from "./CardPatientSearchCU";
import CardPatientOldName from "./CardPatientOldName";
import ModError from "react-lib/appcon/common/ModInfo";
import axios from "axios";
import { useIntl } from "react-intl";

interface PatientSearchBoxCUProps {
 initialValue?: string;
 defaultValue?: string;
 patientListLoading?: boolean;
 patientOldNameLoading?: boolean;
 forcedSelectHN?: string;
 disabled?: boolean;
 fluid?: boolean;
 modalSize?: string;
 patientList?: any[];
 nationalitiesList?: any[];
 patientOldNameDataList?: any[];
 numberOfPage?: number;
 currentPage?: number;
 onInputChange?: () => void;
 getPatientList?: () => void;
 getNationality?: () => void;
 onGetPatientOldName?: () => void;
 onGetPatientByHN?: () => void;
 onPatientListPaginationChange?: () => void;
 onSelectPatient?: () => void;
 onEnter?: () => void;
 controller?: Record<string,any>;
 hideColumnsByAccessor?: any[];
 hideNationality?: boolean;
 hideCitizenID?: boolean;
 clearHnBeforeSearch?: boolean;
 inputChangeAutoSearch?: boolean;
 systemHn8Digit?: boolean;
 modalStyle?: Record<string,any>;
 inputStyle?: Record<string,any>;
}

const DEFAULT_PROPS = {
  // Initial data
  initialValue: "",
  defaultValue: "",
  patientListLoading: false,
  patientOldNameLoading: false,
  forcedSelectHN: null,
  disabled: false,
  fluid: false,
  modalSize: "large",

  // Return data
  patientList: [],
  nationalitiesList: [],
  patientOldNameDataList: [],
  // Pagination
  numberOfPage: 1,
  currentPage: 1,

  // Loading function
  getPatientList: null,
  getNationality: null,
  onGetPatientOldName: null,
  onGetPatientByHN: null,
  onPatientListPaginationChange: null,

  // Callback function
  onSelectPatient: () => {},
  onEnter: () => {},
  onInputChange: () => {},

  // Controller
  controller: null,
  hideColumnsByAccessor: [],
  hideNationality: false,
  hideCitizenID: false,
  clearHnBeforeSearch: false,
  inputChangeAutoSearch: true,
  systemHn8Digit: false,
  modalStyle: {},
  inputStyle: {},
}

const PatientSearchBoxCU = forwardRef<any,PatientSearchBoxCUProps>((propsParam, ref) => {

  const props = {
    ...DEFAULT_PROPS,
    ...Object.fromEntries(Object.entries(propsParam).filter(([_, value])=> value !== undefined))
  }
  const intl = useIntl();
  const isMounted = useRef(true);
  const [openCardPatientSearch, setOpenCardPatientSearch] = useState(false);
  const [openCardPatientOldName, setOpenCardPatientOldName] = useState(false);
  const [openModErrorHN, setOpenModErrorHN] = useState(null);
  const [patientFullName, setPatientFullName] = useState(null);
  const [hnInput, setHNInput] = useState(props.initialValue);
  const inputRef = useRef();

  const [axioSource, setAxioSource] = useState(null);

  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientOldNamesLoading, setPatientOldNamesLoading] = useState(false);
  const [patients, setPatients] = useState<any[]>([]);
  const [nationalities, setNationalities] = useState<any[]>([]);
  const [patientOldNames, setPatientOldNames] = useState<any[]>([]);
  const [numberOfPage, setNumberOfPage] = useState(props.numberOfPage);
  const [currentPage, setCurrentPage] = useState(props.currentPage);

  const [patientId, setPatientId] = useState(null);
  const [isAPILoading, setIsAPILoading] = useState(false);

  const [prevHn, setPrevHn] = useState("");

  useImperativeHandle(ref, () => ({
    focus: () => {
      (inputRef as any).current.focus();
    },
    clear: () => {
      setHNInput("");
    },
    openPatientSearch: () => {
      setOpenCardPatientSearch(true);
    },
    value: () => {
      return hnInput;
    },
    setValue: (hn: any) => {
      setHNInput(hn);
    },
    getPatientId: () => {
      return patientId;
    },
  }));

  const handleCloseCardPatientSearch = () => {
    if (props.clearHnBeforeSearch) {
      setHNInput(prevHn);
    }
    setOpenCardPatientSearch(false);
  };

  const handleCloseCardPatientOldName = () => {
    setOpenCardPatientOldName(false);
  };

  const handleOnChangeHN = (e: any) => {
    console.log(5);
    setHNInput(e.target.value);
  };

  const handleOpenCardPatientSearch = () => {
    if (props.clearHnBeforeSearch) {
      setPrevHn(hnInput);
      setHNInput("");
    }
    setOpenCardPatientSearch(true);
  };

  const handleOpenModPatientOldName = (patientId: any, hn: any, fullName: any) => {
    setOpenCardPatientOldName(true);
    // setHNInput(hn);
    setPatientFullName(fullName);
    if (props.onGetPatientOldName) {
      props.onGetPatientOldName(patientId);
    } else {
      loadPatientOldName(patientId);
    }
  };

  /* For controller */
  const loadPatients = async (params: any) => {
    if (!props.controller) {
      return;
    }
    if (axioSource) {
      (axioSource as any).cancel();
    }
    const source = axios.CancelToken.source();
    setIsAPILoading(true);
    setAxioSource(source as any);

    let pCurrentPage = params.patientCurrentPage;
    if (!params.patientCurrentPage) {
      pCurrentPage = currentPage;
    }
    setCurrentPage(pCurrentPage);

    // Create params
    let newParams = {
      ...params,
      patientCurrentPage: pCurrentPage,
      ...(source && { cancelToken: source.token }),
    };

    const [data, error, totalPage] = await props.controller.getPatientList(newParams);

    if (error === "canceled") {
      console.log("Cancel Not Stop Loading");
    } else {
      console.log(" setIsAPILoading false");
      setIsAPILoading(false);
    }

    if (data) {
      setPatients(data);
      setNumberOfPage(totalPage);
    }
  };

  const loadNationality = async () => {
    if (!props.controller) {
      return;
    }
    const [data, error] = await props.controller.getNatinalities();
    if (data) {
      setNationalities(data);
    }
  };

  const handlePaginationChange = async (params: any) => {
    await loadPatients(params);
  };

  const loadPatientOldName = async (patientId: any) => {
    if (!props.controller) {
      return;
    }
    const [data, error] = await props.controller.getPatientOldName(patientId);
    if (data) {
      setPatientOldNames(data);
    }
  };

  const handleOnKeyPressHN = async (ev: any) => {
    let hn = ev.target.value.toUpperCase();

    if (props.systemHn8Digit) {
      hn = formatHn8Digit(hn);
    }

    if (ev.key === "Enter") {
      ev.preventDefault();
      let [res, err, network] = [null, null, null];
      if (/\d/.test(hn)) {
        // search HN
        if (props.controller) {
          [res, err, network] = await props.controller.getPatientByHN(hn);
        }
        if (props.onGetPatientByHN) {
          [res, err, network] = await props.onGetPatientByHN(hn);
        }
      } else {
        // search Name
        handleOpenCardPatientSearch();
        return;
      }

      if (!(network as any)?.data?.id) {
        // handleOpenCardPatientSearch();
        setOpenModErrorHN(hn)
        setHNInput("")
        setPatientId(null)
      } else {
        const { id, hn, full_name} = (network as any).data;
        setHNInput(hn)
        setPatientId(id)
        props.onEnter && props.onEnter(id, hn, full_name, (network as any).data );
      }
    }
  };

  const formatHn8Digit = (hn: any) => {
    let cloneHN: string = hn;
    if (/(^p|^P)/.test(cloneHN)) {
      cloneHN = cloneHN.toUpperCase();
    } else if (typeof cloneHN === "string") {
      if (cloneHN.includes("-")) {
        cloneHN = cloneHN.replace(/-/g, "");
      } 
      if (cloneHN.length < 8 && cloneHN.length >= 3 && /^\d+$/.test(cloneHN)) {
        const last = cloneHN.slice(2);
        const start = cloneHN.slice(0, 2);
        cloneHN = `${start}${"0".padEnd(8 - cloneHN.length, "0")}${last}`;
      }
    }
    return cloneHN;
  };
  /********************* */

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (props.patientList) {
      setPatients(props.patientList);
    }
  }, [props.patientList]);

  useEffect(() => {
    if (props.nationalitiesList) {
      setNationalities(props.nationalitiesList);
    }
  }, [props.nationalitiesList]);

  useEffect(() => {
    if (props.patientOldNameDataList) {
      setPatientOldNames(props.patientOldNameDataList);
    }
  }, [props.patientOldNameDataList]);

  useEffect(() => {
    if (props.numberOfPage !== null) {
      setNumberOfPage(props.numberOfPage);
    }
  }, [props.numberOfPage]);

  useEffect(() => {
    if (props.currentPage !== null) {
      setCurrentPage(props.currentPage);
    }
  }, [props.currentPage]);

  useEffect(() => {
    if (props.patientListLoading !== null) {
      setPatientsLoading(props.patientListLoading);
    }
  }, [props.patientListLoading]);

  useEffect(() => {
    if (props.patientOldNameLoading) {
      setPatientOldNamesLoading(props.patientOldNameLoading);
    }
  }, [props.patientOldNameLoading]);

  useEffect(() => {
    if (patients.length !== 0) {
      setPatients([]);
    }

    if (props.onSelectPatient) {
      props.onSelectPatient(null, hnInput);
    }
  }, [hnInput]);

  useEffect(() => {
    props.onInputChange?.(hnInput);
  }, [hnInput]);

  // For controller
  useEffect(() => {
    if (props.controller) {
    }
  }, [props.controller]);

  return (
    <React.Fragment>
      <Input
        id={"InputPatientSearchBox"}
        ref={inputRef as any}
        value={hnInput === "" ? props.defaultValue : hnInput}
        disabled={props.disabled}
        // @ts-ignore
        onChange={props.forcedSelectHN ? null : handleOnChangeHN}
        onKeyPress={handleOnKeyPressHN}
        action={{
          icon: props.forcedSelectHN ? "pin" : "search",
          onClick: (event: any) => {
            if (!props.forcedSelectHN) {
              setHNInput("");
              handleOpenCardPatientSearch();
            }
          },
          type: "button",
        }}
        fluid={props.fluid}
      />
      <Modal open={openCardPatientSearch} size={props.modalSize} style={props.modalStyle}>
        <CardPatientSearchCU
          hnPreData={hnInput}
          // @ts-ignore
          isAPILoading={isAPILoading}
          // Data
          nationalitiesList={nationalities}
          patientListLoading={patientsLoading}
          patientList={patients}
          numberOfPage={numberOfPage}
          currentPage={currentPage}
          inputChangeAutoSearch={props.inputChangeAutoSearch}
          systemHn8Digit={props.systemHn8Digit}
          // callback
          onPaginationChange={
            props.onPatientListPaginationChange
              ? props.onPatientListPaginationChange
              : handlePaginationChange
          }
          hideCallBack={handleCloseCardPatientSearch}
          hideColumnsByAccessor={props.hideColumnsByAccessor}
          hideNationality={props.hideNationality}
          hideCitizenID={props.hideCitizenID}
          getNationality={props.getNationality ? props.getNationality : loadNationality}
          getPatientList={props.getPatientList ? props.getPatientList : loadPatients}
          onSelectPatient={(id, hn, name, original) => {
            console.log(id, hn, name);
            setHNInput(hn);
            setPatientId(id);
            setTimeout(() => {
              props.onSelectPatient(id, hn, name, original);
              handleCloseCardPatientSearch();
            }, 0);
          }}
          formatHn8Digit={formatHn8Digit}
          onOpenModPatientOldName={handleOpenModPatientOldName}
          languageUX={props.languageUX}
        />
      </Modal>
      <Modal open={openCardPatientOldName}>
        <CardPatientOldName
          hideCallBack={handleCloseCardPatientOldName}
          hn={hnInput}
          fullName={patientFullName}
          patientOldNameLoading={patientOldNamesLoading}
          patientOldNameDataList={patientOldNames}
          languageUX={props.languageUX}
        />
      </Modal>

      <ModError
        open={openModErrorHN !== null}
        titleColor="red"
        onApprove={() => {
          setOpenModErrorHN(null);
        }}
        style={{ width: "fit-content" }}
        alertText={
          <div style={{ lineHeight: 1.75 }}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div>{`ไม่พบการค้นหาผู้ป่วย `} </div>
              <div
                style={{ fontWeight: "bold", margin: "0 5px", textDecoration: "underline" }}
              >{`HN ${openModErrorHN}`}</div>
              <div>{intl.formatMessage({ id: "หรือ ไม่สามารถเข้าถึงข้อมูลผู้ป่วยได้" })}</div>
            </div>
            <div>{"เนื่องจากไม่ได้อยู่ภายใต้การดูแล ของ"} </div>
            <div>{`${props.userProfile?.full_name}`} </div>
          </div>
        }
      />
    </React.Fragment>
  );
});

PatientSearchBoxCU.displayName = "PatientSearchBoxCU";

export default React.memo(PatientSearchBoxCU);
